/**
 * ISIN helpers.
 *
 * `normalizeIsin` / `isNormalizedIsin` (QP-177 / QL-203): catalog lookups
 * (`getMetaByIsins`, `resolveIsinsInCollection`) match ISINs with exact SQL
 * `IN` comparisons, so every stored ISIN must be in canonical form: trimmed and
 * upper-cased.
 *
 * `isValidIsin` (QL-294) checks the ISO 6166 shape and check digit.
 * `parseIsinAmountLines` reads the "ISIN, amount" lines an advisor pastes into
 * the financial situation Add-by-ISIN dialog, with the amount in the tenant's
 * regional number format.
 */

import {
  decimalSeparatorChar,
  parseNumber,
  type RegionalFormatSettings,
  thousandSeparatorChar,
} from "./regional-format";

/** Normalized ISIN for lookup: trimmed and upper-cased, or null when blank. */
export function normalizeIsin(isin: string | null | undefined): string | null {
  const trimmed = isin?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

/** True when the value is already in canonical form (trimmed, upper-cased). */
export function isNormalizedIsin(isin: string): boolean {
  return isin === isin.trim().toUpperCase();
}

const ISIN_SHAPE = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;

/**
 * True when `value` is a well-formed ISIN: two-letter country prefix, nine
 * alphanumeric characters and a Luhn check digit computed over the letters
 * expanded to numbers (A=10 … Z=35). Case-sensitive — normalize first.
 */
export function isValidIsin(value: string): boolean {
  if (!ISIN_SHAPE.test(value)) return false;

  const digits = [...value].map((char) => Number.parseInt(char, 36).toString()).join("");
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

export type IsinAmountLineError = "invalid_isin" | "missing_amount" | "invalid_amount";

export type ValidIsinAmountLine = {
  /** Upper-cased first token of the line. */
  isin: string;
  amount: number;
  error?: undefined;
};

export type InvalidIsinAmountLine = {
  /** Upper-cased first token of the line. */
  isin: string;
  amount: null;
  error: IsinAmountLineError;
};

export type IsinAmountLine = ValidIsinAmountLine | InvalidIsinAmountLine;

export function isValidIsinAmountLine(line: IsinAmountLine): line is ValidIsinAmountLine {
  return line.error === undefined;
}

// The first token, then ONE separator — a comma or semicolon (spaces around it
// allowed) or a run of whitespace — then the rest of the line. A second comma
// stays in the amount, so "ISIN;,5" is refused rather than read as 5.
const LINE_PATTERN = /^([^,;\s]+)(?:\s*[,;]\s*|\s+)?(.*)$/;
const FRACTION_PATTERN = /^\d{0,2}$/;

/**
 * Stricter than `parseNumber`, which drops grouping separators wherever they
 * appear: the integer part is plain digits, or a leading group of one to three
 * digits followed by groups of exactly three, and the fraction has at most two
 * digits. A figure written in another locale ("50,000" for a decimal-comma
 * tenant, "50000,50" for a decimal-point one) is refused rather than read as
 * 50 or 5 000 050.
 */
function isWellFormedAmount(text: string, settings: RegionalFormatSettings): boolean {
  const decimalChar = decimalSeparatorChar(settings.decimalSeparator);
  const thousandChar = thousandSeparatorChar(settings.thousandSeparator);

  const decimalIndex = text.lastIndexOf(decimalChar);
  const integerText = decimalIndex === -1 ? text : text.slice(0, decimalIndex);
  const fractionText = decimalIndex === -1 ? "" : text.slice(decimalIndex + decimalChar.length);
  if (!FRACTION_PATTERN.test(fractionText)) return false;

  // Plain spaces always group digits, whatever the tenant separator.
  const groups = integerText
    .split(" ")
    .flatMap((part) => (thousandChar === "" ? [part] : part.split(thousandChar)));
  const [first = "", ...rest] = groups;
  if (rest.length === 0) return /^\d+$/.test(first);
  return /^\d{1,3}$/.test(first) && rest.every((group) => /^\d{3}$/.test(group));
}

/**
 * Parse pasted "ISIN<sep>amount" lines, one entry per non-blank line. The
 * separator after the ISIN may be a comma, semicolon, tab or spaces; everything
 * after it is the amount in the tenant's number format, so "50 000,50" and
 * "50,000.50" both read correctly for their tenants. An amount must be above 0,
 * grouped in threes when grouped, with at most two decimals.
 */
export function parseIsinAmountLines(
  text: string,
  settings: RegionalFormatSettings,
): IsinAmountLine[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line): IsinAmountLine => {
      const [, token = "", rest = ""] = LINE_PATTERN.exec(line) ?? [];
      const isin = token.toUpperCase();
      if (!isValidIsin(isin)) return { isin, amount: null, error: "invalid_isin" };

      // Spreadsheet pastes can carry non-breaking spaces as digit grouping.
      const amountText = rest.replace(/[\u00A0\u202F]/g, " ").trim();
      if (amountText === "") return { isin, amount: null, error: "missing_amount" };

      const amount = isWellFormedAmount(amountText, settings)
        ? parseNumber(amountText, settings)
        : null;
      if (amount === null || amount <= 0) return { isin, amount: null, error: "invalid_amount" };
      return { isin, amount };
    });
}
